if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.error = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        const loadingSpinner = this.querySelector('.loading__spinner');
        if (loadingSpinner) loadingSpinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        try {
          await this.validateInventoryBeforeSubmit(formData);

          const response = await fetch(`${routes.cart_add_url}`, config);
          const parsedState = await response.json();

          if (parsedState.status) {
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: parsedState.errors || parsedState.description,
                message: parsedState.message,
              });
            }
            this.handleErrorMessage(parsedState.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButtonText.classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.error && typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: parsedState,
            });
          }
          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  this.cart.renderContents(parsedState);
                });
              },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(parsedState);
          }
        } catch (e) {
          console.error(e);
          this.handleErrorMessage(e.message || window.cartStrings?.error || 'カートに追加できませんでした。');
        } finally {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          const loadingSpinner = this.querySelector('.loading__spinner');
          if (loadingSpinner) loadingSpinner.classList.add('hidden');
        }
      }

      getRequestedQuantity(formData) {
        const parsedQuantity = Number.parseInt(formData.get('quantity'), 10);
        return Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
      }

      getInventoryState() {
        const quantityInput = this.form.querySelector('input[name="quantity"]');
        const inventoryCheck = this.variantIdInput?.dataset.inventoryCheck === 'true'
          || quantityInput?.dataset.inventoryCheck === 'true';
        const inventoryQuantityValue =
          this.variantIdInput?.dataset.inventoryQuantity ?? quantityInput?.dataset.inventoryQuantity;
        const inventoryQuantity = Number.parseInt(inventoryQuantityValue, 10);
        const cartQuantity = Number.parseInt(quantityInput?.dataset.cartQuantity, 10);

        return {
          inventoryCheck,
          inventoryQuantity: Number.isFinite(inventoryQuantity) ? inventoryQuantity : null,
          cartQuantity: Number.isFinite(cartQuantity) ? cartQuantity : 0,
        };
      }

      async fetchCartQuantityForVariant(variantId, fallbackQuantity = 0) {
        const cartPath = window.routes?.cart_url || (typeof routes !== 'undefined' ? routes.cart_url : '/cart');

        try {
          const response = await fetch(`${cartPath}.js`, {
            headers: {
              Accept: 'application/json',
            },
          });
          if (!response.ok) return fallbackQuantity;
          const cart = await response.json();
          if (!cart || !Array.isArray(cart.items)) return fallbackQuantity;

          return cart.items.reduce((total, item) => {
            const itemVariantId = item.variant_id ?? item.id;
            if (String(itemVariantId) !== String(variantId)) return total;
            return total + (Number.parseInt(item.quantity, 10) || 0);
          }, 0);
        } catch (error) {
          console.error('product-form: failed to fetch cart for inventory check', error);
          return fallbackQuantity;
        }
      }

      async validateInventoryBeforeSubmit(formData) {
        const variantId = String(formData.get('id') || this.variantIdInput?.value || '');
        const requestedQuantity = this.getRequestedQuantity(formData);
        const inventoryState = this.getInventoryState();

        if (!variantId || !inventoryState.inventoryCheck || inventoryState.inventoryQuantity === null) return;

        const currentQuantity = await this.fetchCartQuantityForVariant(variantId, inventoryState.cartQuantity);
        if (currentQuantity + requestedQuantity <= inventoryState.inventoryQuantity) return;

        throw new Error('在庫数を超える数量はカートに追加できません。数量を減らしてください。');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) {
          if (errorMessage && typeof window.showNotification === 'function') {
            window.showNotification(errorMessage, 'error');
          }
          return;
        }
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
